import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { POSITIONS, positionAbbrev, positionColor } from "@/lib/playerConstants";
import {
  CRITERIA,
  ageFromGeburtsdatum,
  balanceTeams,
  trainingGroups,
  teamStats,
  criterionAvg,
  groupStats,
} from "@/lib/teamBalance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  Users, Shuffle, Star, ChevronDown, ChevronUp, Sparkles, Trophy, CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TEAM_PALETTE = [
  "border-brand-lighter/60 text-brand-lighter",
  "border-sky-400/60 text-sky-400",
  "border-amber-400/60 text-amber-400",
  "border-rose-400/60 text-rose-400",
  "border-fuchsia-400/60 text-fuchsia-400",
  "border-emerald-400/60 text-emerald-400",
  "border-orange-400/60 text-orange-400",
  "border-cyan-400/60 text-cyan-400",
  "border-violet-400/60 text-violet-400",
  "border-pink-400/60 text-pink-400",
];

function StarsInline({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`h-3 w-3 ${i < value ? "fill-brand-lighter text-brand-lighter" : "fill-transparent text-zinc-700"}`} />
      ))}
    </span>
  );
}

export default function TeamBalance() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [openSelector, setOpenSelector] = useState(true);

  // Filters
  const [positionFilter, setPositionFilter] = useState("all");
  const [jahrgangFilter, setJahrgangFilter] = useState("all");
  const [overallRange, setOverallRange] = useState([1, 10]);

  // Settings
  const [mode, setMode] = useState("A");
  const [teamCount, setTeamCount] = useState(2);
  const [criterionB, setCriterionB] = useState("schnelligkeit");
  const [criterionC, setCriterionC] = useState("overall");

  // Result state
  const [result, setResult] = useState(null); // {mode, teams, names, criterion}

  const [trainings, setTrainings] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, tRes] = await Promise.all([
          api.get("/players"),
          api.get("/trainings"),
        ]);
        setPlayers(pRes.data);
        setTrainings(tRes.data);
      } catch {
        toast.error("Spieler konnten nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // IDs aller Spieler, die heute bei mindestens einem Training als "anwesend" eingetragen sind.
  const todayPresentIds = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const ids = new Set();
    for (const t of trainings) {
      if (t.datum !== todayIso) continue;
      const att = t.attendance || {};
      for (const [pid, entry] of Object.entries(att)) {
        if (entry?.status === "anwesend") ids.add(pid);
      }
    }
    return ids;
  }, [trainings]);

  const selectTodayPresent = () => {
    if (todayPresentIds.size === 0) return;
    const n = new Set(selected);
    todayPresentIds.forEach((id) => n.add(id));
    setSelected(n);
    toast.success(`${todayPresentIds.size} anwesende Spieler von heute übernommen`);
  };

  const jahrgaenge = useMemo(() => {
    const s = new Set(players.map((p) => p.jahrgang));
    return Array.from(s).sort((a, b) => b - a);
  }, [players]);

  const filteredPlayers = useMemo(() => {
    return players
      .filter((p) => {
        if (positionFilter !== "all" && p.position !== positionFilter) return false;
        if (jahrgangFilter !== "all" && String(p.jahrgang) !== jahrgangFilter) return false;
        if (p.overall < overallRange[0] || p.overall > overallRange[1]) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
  }, [players, positionFilter, jahrgangFilter, overallRange]);

  const selectedPlayers = useMemo(
    () => players.filter((p) => selected.has(p.id)),
    [players, selected]
  );

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAllFiltered = () => {
    const n = new Set(selected);
    filteredPlayers.forEach((p) => n.add(p.id));
    setSelected(n);
  };

  const clearSelection = () => setSelected(new Set());

  const allFilteredSelected =
    filteredPlayers.length > 0 && filteredPlayers.every((p) => selected.has(p.id));

  const [generating, setGenerating] = useState(false);

  const handleGenerate = (randomize = false) => {
    if (selectedPlayers.length < teamCount) {
      toast.error(`Mindestens ${teamCount} Spieler auswählen`);
      return;
    }
    setGenerating(true);
    // Defer heavy GA computation so the UI can repaint with the loading state.
    setTimeout(() => {
      try {
        let teams;
        let criterion;
        if (mode === "A") {
          criterion = "overall";
          teams = balanceTeams(selectedPlayers, teamCount, criterion, randomize);
        } else if (mode === "B") {
          criterion = criterionB;
          teams = balanceTeams(selectedPlayers, teamCount, criterion, randomize);
        } else {
          criterion = criterionC;
          teams = trainingGroups(selectedPlayers, teamCount, criterion);
        }
        const names = teams.map((_, i) =>
          mode === "C" ? `Gruppe ${i + 1}` : `Team ${i + 1}`
        );
        setResult({ mode, teams, names, criterion });
        setOpenSelector(false);
        setTimeout(() => {
          document.getElementById("result-section")?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      } finally {
        setGenerating(false);
      }
    }, 30);
  };

  const renameTeam = (i, name) => {
    if (!result) return;
    const newNames = [...result.names];
    newNames[i] = name;
    setResult({ ...result, names: newNames });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-2">Auto Team-Generator</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">Team-Balance</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
          Wähle Spieler, lege Modus und Team-Anzahl fest — der Algorithmus erstellt mit einem Klick faire Teams.
        </p>
      </div>

      {/* Selector Card */}
      <div className="border border-zinc-800 bg-zinc-900/50 mb-6">
        <button
          onClick={() => setOpenSelector((o) => !o)}
          className="w-full flex items-center justify-between p-5 hover:bg-zinc-800/30 transition"
          data-testid="toggle-selector"
        >
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-brand-lighter" />
            <div className="text-left">
              <div className="font-display text-lg uppercase tracking-tight">Spielerauswahl</div>
              <div className="text-xs text-zinc-400" data-testid="selected-count">
                {selected.size} ausgewählt · {filteredPlayers.length} sichtbar
              </div>
            </div>
          </div>
          {openSelector ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-400" />}
        </button>

        {openSelector && (
          <div className="border-t border-zinc-800 p-5 space-y-5">
            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Position</Label>
                <Select value={positionFilter} onValueChange={setPositionFilter}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10" data-testid="tb-filter-position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="all">Alle Positionen</SelectItem>
                    {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Jahrgang</Label>
                <Select value={jahrgangFilter} onValueChange={setJahrgangFilter}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10" data-testid="tb-filter-jahrgang">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem value="all">Alle Jahrgänge</SelectItem>
                    {jahrgaenge.map((j) => <SelectItem key={j} value={String(j)}>{j}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wide text-zinc-400">
                  Overall: <span className="text-brand-lighter">{overallRange[0].toFixed(1)} – {overallRange[1].toFixed(1)}</span>
                </Label>
                <div className="px-1 pt-3">
                  <Slider
                    min={1}
                    max={10}
                    step={0.1}
                    value={overallRange}
                    onValueChange={setOverallRange}
                    className="[&_[role=slider]]:bg-brand-lighter [&_[role=slider]]:border-brand-lighter [&>span:first-child]:bg-zinc-800 [&>span:first-child>span]:bg-brand-lighter"
                    data-testid="tb-overall-slider"
                  />
                </div>
              </div>
            </div>

            {/* Bulk actions */}
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                size="sm"
                variant="outline"
                onClick={allFilteredSelected ? clearSelection : selectAllFiltered}
                className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-brand-lighter uppercase tracking-wider text-xs rounded-sm"
                data-testid="select-all-btn"
              >
                {allFilteredSelected ? "Auswahl leeren" : "Alle auswählen"}
              </Button>
              {todayPresentIds.size > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={selectTodayPresent}
                  className="bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-500/70 hover:text-emerald-200 uppercase tracking-wider text-xs rounded-sm"
                  data-testid="select-today-present-btn"
                  title={`${todayPresentIds.size} Spieler heute anwesend`}
                >
                  <CalendarCheck className="h-3.5 w-3.5 mr-1.5" />
                  Training heute · {todayPresentIds.size}
                </Button>
              )}
              {selected.size > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearSelection}
                  className="text-zinc-400 hover:text-red-400 uppercase tracking-wider text-xs"
                  data-testid="clear-selection-btn"
                >
                  {selected.size} zurücksetzen
                </Button>
              )}
            </div>

            {/* Player list */}
            {loading ? (
              <div className="text-center py-10 text-zinc-500">Lädt…</div>
            ) : filteredPlayers.length === 0 ? (
              <div className="text-center py-10 text-zinc-500 text-sm">Keine Spieler entsprechen den Filtern.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
                {filteredPlayers.map((p) => {
                  const isSel = selected.has(p.id);
                  return (
                    <label
                      key={p.id}
                      htmlFor={`pl-${p.id}`}
                      className={
                        "flex items-center gap-3 p-3 border cursor-pointer transition select-none " +
                        (isSel
                          ? "border-brand-lighter/60 bg-brand-lighter/5"
                          : "border-zinc-800 bg-zinc-950 hover:border-zinc-700")
                      }
                      data-testid={`select-player-${p.id}`}
                    >
                      <Checkbox
                        id={`pl-${p.id}`}
                        checked={isSel}
                        onCheckedChange={() => toggleSelect(p.id)}
                        className="border-zinc-600 data-[state=checked]:bg-brand data-[state=checked]:border-brand data-[state=checked]:text-white"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-zinc-100 truncate">{p.name}</div>
                          <div className="font-display text-base font-bold text-brand-lighter tabular-nums">{p.overall.toFixed(1)}</div>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <div className={`text-[11px] font-medium ${positionColor(p.position)}`}>{positionAbbrev(p.position)} · {p.jahrgang}</div>
                          <StarsInline value={p.talent} />
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Settings + Generate */}
      <div className="border border-zinc-800 bg-zinc-900/50 p-5 mb-6">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="bg-zinc-950 border border-zinc-800 grid grid-cols-1 sm:grid-cols-3 w-full h-auto sm:h-10 gap-1 sm:gap-0 p-1">
            <TabsTrigger value="A" className="data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-wider text-xs h-10 sm:h-auto" data-testid="mode-A-tab">
              Standard
            </TabsTrigger>
            <TabsTrigger value="B" className="data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-wider text-xs h-10 sm:h-auto" data-testid="mode-B-tab">
              Attribut-Fokus
            </TabsTrigger>
            <TabsTrigger value="C" className="data-[state=active]:bg-brand data-[state=active]:text-white uppercase tracking-wider text-xs h-10 sm:h-auto" data-testid="mode-C-tab">
              Trainingsgruppen
            </TabsTrigger>
          </TabsList>

          <TabsContent value="A" className="mt-4 text-sm text-zinc-400">
            Spieler werden anhand der <span className="text-zinc-100">Gesamtbewertung</span> möglichst gleichmäßig auf die Teams verteilt.
          </TabsContent>
          <TabsContent value="B" className="mt-4 space-y-2">
            <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Kriterium</Label>
            <div className="flex flex-wrap gap-2" data-testid="criterion-B-grid">
              {CRITERIA.map((c) => {
                const active = criterionB === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCriterionB(c.key)}
                    className={
                      "min-h-[40px] px-3 rounded-md border text-xs sm:text-sm font-medium transition " +
                      (active
                        ? "bg-brand border-brand text-white"
                        : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100")
                    }
                    data-testid={`criterion-B-${c.key}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-zinc-500 pt-1">Alle übrigen Attribute sind zweitrangig.</div>
          </TabsContent>
          <TabsContent value="C" className="mt-4 space-y-2">
            <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Sortierkriterium</Label>
            <div className="flex flex-wrap gap-2" data-testid="criterion-C-grid">
              {CRITERIA.map((c) => {
                const active = criterionC === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCriterionC(c.key)}
                    className={
                      "min-h-[40px] px-3 rounded-md border text-xs sm:text-sm font-medium transition " +
                      (active
                        ? "bg-brand border-brand text-white"
                        : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100")
                    }
                    data-testid={`criterion-C-${c.key}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-zinc-500 pt-1">Stärkste Spieler in Gruppe 1, schwächste in der letzten Gruppe.</div>
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 mt-5 pt-5 border-t border-zinc-800">
          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-zinc-400">
                {mode === "C" ? "Anzahl Gruppen" : "Anzahl Teams"}: <span className="text-brand-lighter font-semibold">{teamCount}</span>
              </Label>
              {selectedPlayers.length > 0 && (() => {
                const base = Math.floor(selectedPlayers.length / teamCount);
                const rem = selectedPlayers.length % teamCount;
                const sizes = rem === 0 ? `${base}` : `${base}–${base + 1}`;
                return (
                  <span className="text-[10px] uppercase tracking-wide text-zinc-400" data-testid="players-per-team-hint">
                    Spieler je {mode === "C" ? "Gruppe" : "Team"}: <span className="text-brand-lighter font-semibold normal-case">{sizes}</span>
                    <span className="text-zinc-600 normal-case"> · {selectedPlayers.length} ausgewählt</span>
                  </span>
                );
              })()}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <button
                  key={n}
                  onClick={() => setTeamCount(n)}
                  className={
                    "h-10 w-10 sm:h-11 sm:w-11 border font-display font-bold text-base transition " +
                    (teamCount === n
                      ? "bg-brand border-brand text-white"
                      : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-brand-lighter")
                  }
                  data-testid={`team-count-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="bg-brand hover:bg-brand-light text-white uppercase tracking-wider font-semibold h-12 px-6 w-full sm:w-auto rounded-sm disabled:opacity-70"
              data-testid="generate-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {generating
                ? "Berechne…"
                : mode === "C"
                  ? "Gruppen erstellen"
                  : "Teams generieren"}
            </Button>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div id="result-section">
          {result.mode === "C" ? (
            <GroupsView result={result} renameTeam={renameTeam} />
          ) : (
            <TeamsView
              result={result}
              renameTeam={renameTeam}
              onReroll={() => handleGenerate(true)}
              onStartTournament={() => {
                const teamsForTournament = result.teams.map((team, i) => ({
                  name: result.names[i],
                  players: team,
                }));
                const pool = result.teams.flat();
                navigate("/turniere/neu", {
                  state: {
                    teams: teamsForTournament,
                    playerPool: pool,
                    balanceCriterion: result.criterion || "overall",
                  },
                });
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TeamsView({ result, renameTeam, onReroll, onStartTournament }) {
  const { teams, names, criterion } = result;
  const criterionLabel = CRITERIA.find((c) => c.key === criterion)?.label || "Wert";

  const stats = teams.map((t) => ({
    ...teamStats(t),
    critAvg: criterionAvg(t, criterion),
  }));
  const avgs = stats.map((s) => s.critAvg);
  const maxAvg = Math.max(...avgs);
  const minAvg = Math.min(...avgs);
  const strongestIdx = avgs.indexOf(maxAvg);
  const weakestIdx = avgs.lastIndexOf(minAvg);
  const diff = Math.round((maxAvg - minAvg) * 10) / 10;
  const perfect = diff === 0;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-1">Ergebnis</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
            {teams.length} balancierte Teams
          </h2>
          <div className="text-xs text-zinc-500 mt-1">Kriterium: {criterionLabel}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onReroll}
            variant="outline"
            className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 uppercase tracking-wider font-semibold rounded-sm h-11"
            data-testid="reroll-btn"
          >
            <Shuffle className="h-4 w-4 mr-2" />
            Neue Variante
          </Button>
          <Button
            onClick={onStartTournament}
            className="bg-brand hover:bg-brand-light text-white uppercase tracking-wider font-semibold rounded-sm h-11"
            data-testid="start-tournament-btn"
          >
            <Trophy className="h-4 w-4 mr-2" />
            Turnier starten
          </Button>
        </div>
      </div>

      {/* Comparison summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {perfect ? (
          <>
            <SummaryCard label="Balance" value="Perfekt" sub={`Alle Teams Ø ${maxAvg.toFixed(1)} ${criterionLabel}`} accent="text-brand-lighter" testId="strongest" />
            <SummaryCard label="Streuung" value="0.0" sub="Keine Differenz zwischen Teams" accent="text-brand-lighter" testId="weakest" />
            <SummaryCard label="Differenz" value="0.0" sub="zwischen Top und Bottom" accent="text-brand-lighter" testId="diff" />
          </>
        ) : (
          <>
            <SummaryCard label="Stärkstes Team" value={names[strongestIdx]} sub={`Ø ${maxAvg.toFixed(1)} ${criterionLabel}`} accent="text-brand-lighter" testId="strongest" />
            <SummaryCard label="Schwächstes Team" value={names[weakestIdx]} sub={`Ø ${minAvg.toFixed(1)} ${criterionLabel}`} accent="text-rose-400" testId="weakest" />
            <SummaryCard label="Differenz" value={diff.toFixed(1)} sub={`zwischen Top und Bottom`} accent="text-amber-400" testId="diff" />
          </>
        )}
      </div>

      {/* Team cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {teams.map((team, i) => (
          <TeamCard
            key={i}
            team={team}
            name={names[i]}
            onRename={(n) => renameTeam(i, n)}
            stats={stats[i]}
            criterion={criterion}
            criterionLabel={criterionLabel}
            colorClass={TEAM_PALETTE[i % TEAM_PALETTE.length]}
            index={i}
          />
        ))}
      </div>
    </div>
  );
}

function GroupsView({ result, renameTeam }) {
  const { teams, names, criterion } = result;
  const criterionLabel = CRITERIA.find((c) => c.key === criterion)?.label || "Wert";

  return (
    <div>
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-1">Ergebnis</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
          {teams.length} Trainingsgruppen
        </h2>
        <div className="text-xs text-zinc-500 mt-1">
          Sortiert nach <span className="text-zinc-300">{criterionLabel}</span> · Stärkste in Gruppe 1
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {teams.map((group, i) => {
          const gs = groupStats(group, criterion);
          return (
            <div key={i} className={`border ${TEAM_PALETTE[i % TEAM_PALETTE.length].split(" ")[0]} bg-zinc-900/50 p-5`} data-testid={`group-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <NameEditor name={names[i]} onChange={(n) => renameTeam(i, n)} testId={`group-name-${i}`} />
                <div className={`text-[10px] uppercase tracking-wider ${TEAM_PALETTE[i % TEAM_PALETTE.length].split(" ")[1]}`}>#{i + 1}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-zinc-800">
                <Stat label="Anz." value={gs.count} />
                <Stat label="Ø" value={gs.avg.toFixed(1)} />
                <Stat label="Min/Max" value={`${gs.min}/${gs.max}`} />
              </div>
              <PlayerRows team={group} criterion={criterion} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamCard({ team, name, onRename, stats, criterion, criterionLabel, colorClass, index }) {
  const [borderColor, textColor] = colorClass.split(" ");
  return (
    <div className={`border ${borderColor} bg-zinc-900/50 p-5`} data-testid={`team-card-${index}`}>
      <div className="flex items-center justify-between mb-3">
        <NameEditor name={name} onChange={onRename} testId={`team-name-${index}`} />
        <div className={`text-[10px] uppercase tracking-wider ${textColor}`}>#{index + 1}</div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4 pb-4 border-b border-zinc-800">
        <Stat label="Anz." value={stats.count} />
        <Stat label="Ø Overall" value={stats.avg.toFixed(1)} />
        <Stat label="Summe" value={stats.sum.toFixed(1)} />
        <Stat label="Ø Alter" value={stats.avgAge.toFixed(1)} />
      </div>
      {criterion !== "overall" && (
        <div className="mb-3 pb-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400">Ø {criterionLabel}</div>
          <div className={`font-display text-xl font-bold ${textColor} tabular-nums`} data-testid={`team-crit-avg-${index}`}>
            {stats.critAvg.toFixed(1)}
          </div>
        </div>
      )}
      <PlayerRows team={team} criterion={criterion} />
    </div>
  );
}

function PlayerRows({ team, criterion }) {
  return (
    <div className="space-y-1.5">
      {team.length === 0 && <div className="text-xs text-zinc-500">Keine Spieler.</div>}
      {team.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-2 py-1">
          <div className="min-w-0 flex-1">
            <div className="text-sm text-zinc-100 truncate">{p.name}</div>
            <div className={`text-[11px] font-medium tracking-wide ${positionColor(p.position)}`}>
              {positionAbbrev(p.position)} · {p.jahrgang}
            </div>
          </div>
          <div className="font-display text-base font-bold text-brand-lighter tabular-nums">
            {criterion === "alter"
              ? ageFromGeburtsdatum(p.geburtsdatum)
              : criterion === "overall"
                ? p.overall.toFixed(1)
                : p.attribute[criterion]}
          </div>
        </div>
      ))}
    </div>
  );
}

function NameEditor({ name, onChange, testId }) {
  return (
    <Input
      value={name}
      onChange={(e) => onChange(e.target.value)}
      className="font-display text-lg font-bold uppercase tracking-tight bg-transparent border-0 border-b border-transparent hover:border-zinc-700 focus-visible:ring-0 focus-visible:border-brand-lighter px-0 h-auto py-1 text-zinc-100"
      data-testid={testId}
    />
  );
}

function SummaryCard({ label, value, sub, accent, testId }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/50 p-4" data-testid={`summary-${testId}`}>
      <div className="text-[10px] uppercase tracking-wide text-zinc-400">{label}</div>
      <div className={`font-display text-2xl font-bold uppercase tracking-tight mt-1 ${accent}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-display text-base font-bold text-zinc-100 tabular-nums">{value}</div>
    </div>
  );
}
